import React, { useEffect, useState } from 'react';
import {
  collection, getDocs, query, where,
  addDoc, serverTimestamp, getDoc, doc
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';

// ─── Default stage definitions ────────────────────────────────
const DEFAULT_CUSTOMER_STAGES = [
  {
    id: 'awareness',
    label: 'Awareness',
    icon: '👁',
    color: '#7c6fcd',
    description: 'How do customers discover your product?',
    fields: [
      { id: 'touchpoints', label: 'Key touchpoints', placeholder: 'e.g. social media, word of mouth, NGO referrals, government tenders…' },
      { id: 'pain_points', label: 'Pain points', placeholder: 'What barriers exist to discovery? What problems are they aware of?' },
      { id: 'opportunities', label: 'Opportunities', placeholder: 'How could you improve awareness reach?' },
      { id: 'decision_makers', label: 'Key decision makers', placeholder: 'Who influences the purchase decision at this stage?' },
    ],
  },
  {
    id: 'evaluation',
    label: 'Evaluation',
    icon: '🔍',
    color: '#0369a1',
    description: 'How do customers assess your product?',
    fields: [
      { id: 'touchpoints', label: 'Key touchpoints', placeholder: 'e.g. demo requests, pilot programs, peer references…' },
      { id: 'pain_points', label: 'Pain points', placeholder: 'What concerns or objections arise during evaluation?' },
      { id: 'opportunities', label: 'Opportunities', placeholder: 'How could you improve the evaluation experience?' },
      { id: 'decision_makers', label: 'Key decision makers', placeholder: 'Who signs off on the evaluation?' },
    ],
  },
  {
    id: 'purchase',
    label: 'Purchase / Procurement',
    icon: '💰',
    color: '#166534',
    description: 'How do customers buy or procure your product?',
    fields: [
      { id: 'touchpoints', label: 'Key touchpoints', placeholder: 'e.g. procurement process, contract negotiation, payment terms…' },
      { id: 'pain_points', label: 'Pain points', placeholder: 'What slows down or blocks the purchase?' },
      { id: 'opportunities', label: 'Opportunities', placeholder: 'How could you streamline procurement?' },
      { id: 'decision_makers', label: 'Key decision makers', placeholder: 'Who approves the final purchase?' },
    ],
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    icon: '🚀',
    color: '#0369a1',
    description: 'How do customers get set up and started?',
    fields: [
      { id: 'touchpoints', label: 'Key touchpoints', placeholder: 'e.g. implementation support, training, account setup…' },
      { id: 'pain_points', label: 'Pain points', placeholder: 'What causes friction during onboarding?' },
      { id: 'opportunities', label: 'Opportunities', placeholder: 'How could you improve time-to-value?' },
      { id: 'decision_makers', label: 'Key decision makers', placeholder: 'Who manages the rollout?' },
    ],
  },
  {
    id: 'renewal',
    label: 'Renewal / Advocacy',
    icon: '🔄',
    color: '#065f46',
    description: 'How do customers renew and refer others?',
    fields: [
      { id: 'touchpoints', label: 'Key touchpoints', placeholder: 'e.g. renewal conversations, impact reports, referral requests…' },
      { id: 'pain_points', label: 'Pain points', placeholder: 'What risks renewal failure or churn?' },
      { id: 'opportunities', label: 'Opportunities', placeholder: 'How could you drive renewal and referrals?' },
      { id: 'decision_makers', label: 'Key decision makers', placeholder: 'Who champions renewal internally?' },
    ],
  },
];

const DEFAULT_USER_STAGES = [
  {
    id: 'discovery',
    label: 'Discovery',
    icon: '🌟',
    color: '#7c6fcd',
    description: 'How do learners/teachers first encounter your product?',
    fields: [
      { id: 'experience', label: 'User experience', placeholder: 'How do learners/teachers find out about the product?' },
      { id: 'friction', label: 'Friction points', placeholder: 'What barriers exist to discovery for users?' },
      { id: 'retention_drivers', label: 'Engagement drivers', placeholder: 'What initially attracts them?' },
      { id: 'outcome_evidence', label: 'Outcome evidence', placeholder: 'What signals show discovery is working?' },
    ],
  },
  {
    id: 'first_use',
    label: 'First Use',
    icon: '▶️',
    color: '#0369a1',
    description: 'What is the first experience like for users?',
    fields: [
      { id: 'experience', label: 'User experience', placeholder: 'What happens when a learner/teacher first uses the product?' },
      { id: 'friction', label: 'Friction points', placeholder: 'What causes drop-off or confusion at first use?' },
      { id: 'retention_drivers', label: 'Engagement drivers', placeholder: 'What keeps them coming back after first use?' },
      { id: 'outcome_evidence', label: 'Outcome evidence', placeholder: 'What indicates a successful first session?' },
    ],
  },
  {
    id: 'engagement',
    label: 'Regular Engagement',
    icon: '🔥',
    color: '#166534',
    description: 'How do users become regular, active users?',
    fields: [
      { id: 'experience', label: 'User experience', placeholder: 'What does regular usage look like?' },
      { id: 'friction', label: 'Friction points', placeholder: 'What causes disengagement or irregular usage?' },
      { id: 'retention_drivers', label: 'Retention drivers', placeholder: 'What builds habit and regular usage?' },
      { id: 'outcome_evidence', label: 'Outcome evidence', placeholder: 'What learning outcomes are emerging at this stage?' },
    ],
  },
  {
    id: 'mastery',
    label: 'Mastery / Completion',
    icon: '🎯',
    color: '#0369a1',
    description: 'How do users reach their learning goals?',
    fields: [
      { id: 'experience', label: 'User experience', placeholder: 'What does mastery or completion look like?' },
      { id: 'friction', label: 'Friction points', placeholder: 'What prevents users from reaching completion?' },
      { id: 'retention_drivers', label: 'Success drivers', placeholder: 'What helps users achieve their goals?' },
      { id: 'outcome_evidence', label: 'Outcome evidence', placeholder: 'How do you measure learning improvement?' },
    ],
  },
  {
    id: 'advocacy',
    label: 'Advocacy',
    icon: '📢',
    color: '#065f46',
    description: 'How do users become advocates and refer others?',
    fields: [
      { id: 'experience', label: 'User experience', placeholder: 'What prompts users to recommend the product?' },
      { id: 'friction', label: 'Friction points', placeholder: 'What prevents advocacy?' },
      { id: 'retention_drivers', label: 'Advocacy drivers', placeholder: 'What turns users into active promoters?' },
      { id: 'outcome_evidence', label: 'Outcome evidence', placeholder: 'How do you track referrals and advocacy?' },
    ],
  },
];


export default function JourneyMapPage() {
  const { profile } = useAuth();
  const [companyId, setCompanyId]     = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [versions, setVersions]       = useState([]);
  const [activeVersion, setActiveVersion] = useState(null);
  const [data, setData]               = useState({ customer: {}, user: {} });
  const [drafts, setDrafts]           = useState({ customer: {}, user: {} });
  const [customerStages, setCustomerStages] = useState(DEFAULT_CUSTOMER_STAGES);
  const [userStages, setUserStages]   = useState(DEFAULT_USER_STAGES);
  const [comments, setComments]       = useState({});
  const [activeComment, setActiveComment] = useState(null);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(null);
  const [activeTrack, setActiveTrack] = useState('customer'); // 'customer' | 'user'

  const isReadOnly = profile?.role === 'client' || profile?.role === 'team';

  // Load company
  useEffect(() => {
    if (!profile?.uid) return;
    async function loadCompany() {
      try {
        let cId = null, cName = '';
        if (profile.role === 'beneficiary') {
          const q = query(collection(db, 'companies'), where('memberUids', 'array-contains', profile.uid));
          const snap = await getDocs(q);
          if (!snap.empty) { cId = snap.docs[0].id; cName = snap.docs[0].data().name; }
        } else if (profile.role === 'coach') {
          const q = query(collection(db, 'companies'), where('coachId', '==', profile.uid));
          const snap = await getDocs(q);
          if (!snap.empty) { cId = snap.docs[0].id; cName = snap.docs[0].data().name; }
        }
        if (cId) { setCompanyId(cId); setCompanyName(cName); }
      } catch (err) { console.error(err); }
    }
    loadCompany();
  }, [profile]);

  // Load versions + cohort config
  useEffect(() => {
    if (!companyId) return;
    async function load() {
      try {
        // Load cohort stages if configured
        const compDoc = await getDoc(doc(db, 'companies', companyId));
        const comp = compDoc.data();
        if (comp?.cohortId) {
          const cohortDoc = await getDoc(doc(db, 'cohorts', comp.cohortId));
          const cohortData = cohortDoc.data();
          if (cohortData?.journeyStages?.customer) setCustomerStages(cohortData.journeyStages.customer);
          if (cohortData?.journeyStages?.user) setUserStages(cohortData.journeyStages.user);
        }

        // Load CJM versions
        const snap = await getDocs(
          query(
            collection(db, 'companies', companyId, 'canvasVersions'),
            where('type', '==', 'cjm')
          )
        );
        const vList = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a,b) => {
            const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
            const db2 = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
            return db2 - da;
          });
        setVersions(vList);

        if (vList.length > 0) {
          setActiveVersion(vList[0]);
          setData(vList[0].data || { customer: {}, user: {} });
          setDrafts(vList[0].data || { customer: {}, user: {} });
          if (vList.length > 1) setShowHistory(true);
        }

        // Load comments
        await loadComments(companyId);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [companyId]);

  const loadComments = async (cId) => {
    try {
      const snap = await getDocs(collection(db, 'companies', cId, 'journeyComments'));
      const commentMap = {};
      snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a,b) => {
          const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
          const db2 = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
          return da - db2;
        })
        .forEach(c => {
          const key = `${c.track}_${c.stageId}`;
          if (!commentMap[key]) commentMap[key] = [];
          commentMap[key].push(c);
        });
      setComments(commentMap);
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    if (!companyId || saving) return;
    setSaving(true);
    try {
      const versionData = {
        companyId,
        type: 'cjm',
        data: drafts,
        status: 'draft',
        savedBy: profile.uid,
        savedByName: profile.displayName,
        createdAt: serverTimestamp(),
      };
      const ref = await addDoc(
        collection(db, 'companies', companyId, 'canvasVersions'),
        versionData
      );
      const newVersion = { id: ref.id, ...versionData, createdAt: new Date() };
      setVersions(prev => [newVersion, ...prev]);
      setActiveVersion(newVersion);
      setData(drafts);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const postComment = async (track, stageId) => {
    const key = `${track}_${stageId}`;
    const text = commentDrafts[key]?.trim();
    if (!text || !companyId) return;
    try {
      const commentData = {
        track, stageId, key,
        body: text,
        authorUid: profile.uid,
        authorName: profile.displayName,
        authorRole: profile.role,
        createdAt: serverTimestamp(),
      };
      const ref = await addDoc(
        collection(db, 'companies', companyId, 'journeyComments'),
        commentData
      );
      setComments(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), { id: ref.id, ...commentData, createdAt: new Date() }]
      }));
      setCommentDrafts(prev => ({ ...prev, [key]: '' }));
    } catch (err) { console.error(err); }
  };

  const updateDraft = (track, stageId, fieldId, value) => {
    setDrafts(prev => ({
      ...prev,
      [track]: {
        ...(prev[track] || {}),
        [stageId]: {
          ...(prev[track]?.[stageId] || {}),
          [fieldId]: value,
        }
      }
    }));
  };

  const loadVersion = (version) => setPreviewVersion(version);

  const restoreVersion = (version) => {
    setActiveVersion(version);
    setData(version.data || { customer: {}, user: {} });
    setDrafts(version.data || { customer: {}, user: {} });
    setPreviewVersion(null);
  };

  const hasChanges = JSON.stringify(drafts) !== JSON.stringify(data);

  const stages = activeTrack === 'customer' ? customerStages : userStages;

  if (loading) return (
    <DashboardLayout>
      <div className="gm-loading"><div className="gm-spinner" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="gm-page gm-journey-page">
        <div className="gm-page-header">
          <div>
            <h1 className="gm-page-title">Customer & User Journey Map</h1>
            <p className="gm-page-sub">{companyName}</p>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="gm-btn gm-btn-outline gm-btn-sm" onClick={() => setShowHistory(!showHistory)}>
              {showHistory ? 'Hide history' : versions.length > 0 ? `History (${versions.length})` : 'No versions yet'}
            </button>
            {!isReadOnly && (
              <button className="gm-btn gm-btn-primary" onClick={handleSave} disabled={saving || !hasChanges}>
                {saving ? 'Saving…' : 'Save version'}
              </button>
            )}
            {saved && <span className="gm-save-success">✓ Version saved</span>}
          </div>
        </div>

        {/* Version history */}
        {showHistory && versions.length > 0 && (
          <div className="gm-canvas-history">
            <h3>Version history</h3>
            {versions.map((v, idx) => {
              const date = v.createdAt?.toDate ? v.createdAt.toDate() : new Date(v.createdAt);
              return (
                <div key={v.id}
                  className={`gm-canvas-history-item ${activeVersion?.id === v.id ? 'gm-canvas-history-item--active' : ''}`}
                  onClick={() => loadVersion(v)}>
                  <span className="gm-canvas-history-date">
                    {date.toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                  </span>
                  <span className="gm-canvas-history-by">by {v.savedByName}</span>
                  {idx === 0 && <span className="gm-validated-badge">Latest</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* Track selector */}
        <div className="gm-journey-track-selector">
          <button
            className={`gm-journey-track-btn ${activeTrack === 'customer' ? 'gm-journey-track-btn--active' : ''}`}
            onClick={() => setActiveTrack('customer')}
          >
            💼 Customer Journey
            <span className="gm-journey-track-sub">Who pays — parents, schools, NGOs, governments</span>
          </button>
          <button
            className={`gm-journey-track-btn ${activeTrack === 'user' ? 'gm-journey-track-btn--active' : ''}`}
            onClick={() => setActiveTrack('user')}
          >
            🎓 User Journey
            <span className="gm-journey-track-sub">Who learns — students, teachers, facilitators</span>
          </button>
        </div>

        {/* Journey stages — horizontal scroll on mobile */}
        <div className="gm-journey-grid">
          {stages.map((stage, idx) => {
            const key = `${activeTrack}_${stage.id}`;
            const stageData = drafts[activeTrack]?.[stage.id] || {};
            const stageComments = comments[key] || [];
            const isCommentOpen = activeComment === key;
            const hasContent = Object.values(stageData).some(v => v);

            return (
              <div key={stage.id}
                className={`gm-journey-stage ${hasContent ? 'gm-journey-stage--filled' : ''}`}
                style={{ borderTop: `3px solid ${stage.color}` }}>

                {/* Stage header */}
                <div className="gm-journey-stage-header">
                  <span className="gm-journey-stage-num" style={{ background: stage.color }}>
                    {idx + 1}
                  </span>
                  <span className="gm-journey-stage-icon">{stage.icon}</span>
                  <div className="gm-journey-stage-title">{stage.label}</div>
                  {/* Comment toggle */}
                  <button
                    className={`gm-bmc-comment-btn ${stageComments.length > 0 ? 'gm-bmc-comment-btn--has' : ''}`}
                    onClick={() => setActiveComment(isCommentOpen ? null : key)}
                    title={`${stageComments.length} comment${stageComments.length !== 1 ? 's' : ''}`}
                  >
                    💬{stageComments.length > 0 ? ` ${stageComments.length}` : ''}
                  </button>
                </div>
                <div className="gm-journey-stage-desc">{stage.description}</div>

                {/* Stage fields */}
                <div className="gm-journey-stage-fields">
                  {stage.fields.map(field => (
                    <div key={field.id} className="gm-journey-field">
                      <label className="gm-journey-field-label">{field.label}</label>
                      {isReadOnly ? (
                        <div className="gm-bmc-block-text" style={{ minHeight: 60 }}>
                          {stageData[field.id] || <span className="gm-bmc-block-empty">Not filled in</span>}
                        </div>
                      ) : (
                        <textarea
                          className="gm-journey-field-input"
                          placeholder={field.placeholder}
                          value={stageData[field.id] || ''}
                          onChange={e => updateDraft(activeTrack, stage.id, field.id, e.target.value)}
                          rows={3}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Sticky comments */}
                {isCommentOpen && (
                  <div className="gm-bmc-sticky">
                    <div className="gm-bmc-sticky-header">
                      <span>Comments — {stage.label}</span>
                      <button className="gm-btn-icon" onClick={() => setActiveComment(null)}>✕</button>
                    </div>
                    <div className="gm-bmc-sticky-comments">
                      {stageComments.length === 0 && <p className="gm-comments-empty">No comments yet.</p>}
                      {stageComments.map((c, i) => {
                        const date = c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt);
                        return (
                          <div key={c.id || i} className="gm-comment">
                            <div className="gm-comment-meta">
                              <span className="gm-comment-author">{c.authorName}</span>
                              <span className={`gm-comment-role gm-action-assignee--${c.authorRole}`}>
                                {c.authorRole}
                              </span>
                              <span className="gm-comment-time">
                                {date.toLocaleDateString('en-GB', { day:'numeric', month:'short' })}
                              </span>
                            </div>
                            <div className="gm-comment-body">{c.body}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="gm-comment-form">
                      <textarea
                        placeholder="Add a comment…"
                        value={commentDrafts[key] || ''}
                        onChange={e => setCommentDrafts(prev => ({ ...prev, [key]: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); postComment(activeTrack, stage.id); }}}
                        rows={2}
                      />
                      <button className="gm-btn gm-btn-primary gm-btn-sm"
                        onClick={() => postComment(activeTrack, stage.id)}
                        disabled={!commentDrafts[key]?.trim()}>
                        Post
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Unsaved changes bar */}
        {!isReadOnly && hasChanges && (
          <div className="gm-canvas-save-bar">
            <span>You have unsaved changes</span>
            <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save version'}
            </button>
          </div>
        )}

        {/* Version preview modal */}
        {previewVersion && (
          <div className="gm-canvas-preview-overlay" onClick={() => setPreviewVersion(null)}>
            <div className="gm-canvas-preview-modal" onClick={e => e.stopPropagation()}>
              <div className="gm-canvas-preview-header">
                <div>
                  <div className="gm-canvas-preview-title">Version preview</div>
                  <div className="gm-canvas-preview-meta">
                    {previewVersion.createdAt?.toDate
                      ? previewVersion.createdAt.toDate().toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })
                      : ''} · {previewVersion.savedByName}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={() => restoreVersion(previewVersion)}>
                    Restore this version
                  </button>
                  <button className="gm-btn gm-btn-outline gm-btn-sm" onClick={() => setPreviewVersion(null)}>Close</button>
                </div>
              </div>
              <div className="gm-canvas-preview-body">
                <JourneyPreview data={previewVersion.data || {}} customerStages={customerStages} userStages={userStages} />
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// ─── Journey preview (read-only, used in version history modal) ──
function JourneyPreview({ data, customerStages, userStages }) {
  const [track, setTrack] = useState('customer');
  const stages = track === 'customer' ? customerStages : userStages;

  return (
    <div>
      <div className="gm-journey-track-selector" style={{ marginBottom: 16 }}>
        <button className={`gm-journey-track-btn ${track === 'customer' ? 'gm-journey-track-btn--active' : ''}`}
          onClick={() => setTrack('customer')}>💼 Customer Journey</button>
        <button className={`gm-journey-track-btn ${track === 'user' ? 'gm-journey-track-btn--active' : ''}`}
          onClick={() => setTrack('user')}>🎓 User Journey</button>
      </div>
      <div className="gm-journey-grid">
        {stages.map(stage => {
          const stageData = data[track]?.[stage.id] || {};
          return (
            <div key={stage.id} className="gm-journey-stage" style={{ borderTop: `3px solid ${stage.color}` }}>
              <div className="gm-journey-stage-header">
                <span className="gm-journey-stage-icon">{stage.icon}</span>
                <div className="gm-journey-stage-title">{stage.label}</div>
              </div>
              <div className="gm-journey-stage-fields">
                {stage.fields.map(field => (
                  <div key={field.id} className="gm-journey-field">
                    <label className="gm-journey-field-label">{field.label}</label>
                    <div className="gm-bmc-block-text" style={{ minHeight: 40, fontSize: '0.78rem' }}>
                      {stageData[field.id] || <span className="gm-bmc-block-empty">Empty</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
